import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button, IconButton } from './Button.tsx';

export interface SheetProps {
  open: boolean;
  onClose(): void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** Optional extra controls in the header (e.g. a menu). */
  headerActions?: ReactNode;
}

/**
 * Modal sheet built on the native <dialog> element: focus is trapped and
 * restored by the browser, Escape closes it, and the backdrop dims the page.
 * Bottom sheet on phones, centered dialog on larger screens.
 */
export function Sheet({ open, onClose, title, children, footer, wide, headerActions }: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } else if (!open && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={`ui-sheet${wide ? ' ui-sheet--wide' : ''}`}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onMouseDown={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      {open && (
        <>
          <div className="ui-sheet__head">
            <h2 id={titleId} className="ui-sheet__title">
              {title}
            </h2>
            {headerActions}
            <IconButton label="Close" onClick={onClose}>
              <X aria-hidden="true" />
            </IconButton>
          </div>
          <div className="ui-sheet__body">{children}</div>
          {footer && <div className="ui-sheet__foot">{footer}</div>}
        </>
      )}
    </dialog>
  );
}

interface ConfirmRequest {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  resolve(ok: boolean): void;
}

const ConfirmContext = createContext<(req: Omit<ConfirmRequest, 'resolve'>) => Promise<boolean>>(async () => false);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [req, setReq] = useState<ConfirmRequest | null>(null);
  const confirm = useCallback((r: Omit<ConfirmRequest, 'resolve'>) => new Promise<boolean>((resolve) => setReq({ ...r, resolve })), []);
  const close = (ok: boolean) => {
    req?.resolve(ok);
    setReq(null);
  };
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Sheet
        open={req !== null}
        onClose={() => close(false)}
        title={req?.title ?? ''}
        footer={
          <>
            <Button onClick={() => close(false)}>Cancel</Button>
            <Button variant={req?.danger ? 'danger' : 'primary'} onClick={() => close(true)} autoFocus>
              {req?.confirmLabel ?? 'Confirm'}
            </Button>
          </>
        }
      >
        {typeof req?.message === 'string' ? <p>{req.message}</p> : req?.message}
      </Sheet>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmContext);
}
